// src/web/lib/csrf.ts
//
// Shared CSRF token cache for the frontend. The server issues a stateless HMAC
// token on every GET response (`x-csrf-token`) and requires it back on
// state-changing requests. The token is global (not session-bound) with a 1h
// TTL, so a single cached value works for every mutation; we capture it from
// every response and lazily fetch one if a mutation fires before any GET.
//
// Both clients share this cache: the Eden treaty client (api.ts) and the raw
// fetch client (api-fetch.ts).

let cachedToken: string | null = null

/** Store a token seen on a response header (no-op for null/empty). */
export function captureCsrfToken(token: string | null | undefined): void {
	if (token) cachedToken = token
}

/** The cached token, or null if none seen yet. */
export function getCachedCsrfToken(): string | null {
	return cachedToken
}

/**
 * Return a usable token, fetching one from a cheap GET (/health) if the cache
 * is cold. Safe to call on every mutation — only the cold path hits the network.
 */
export async function ensureCsrfToken(apiBase: string): Promise<string> {
	if (cachedToken) return cachedToken
	try {
		const res = await fetch(`${apiBase}/health`, { credentials: 'include' })
		captureCsrfToken(res.headers.get('x-csrf-token'))
	} catch {
		// Network error — return empty; the mutation will surface the real failure.
	}
	return cachedToken ?? ''
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export function isMutationMethod(method: string | undefined): boolean {
	return MUTATION_METHODS.has((method ?? 'GET').toUpperCase())
}
